<div class="sop-shell p-2 p-lg-3">
  <div class="hero-card mb-3">
    <div class="hero-copy">
      <div class="d-flex align-items-center gap-3">
        <span class="icon-pill">
          <lucide-icon [img]="ReceiptTextIcon" [size]="16"></lucide-icon>
        </span>
        <div>
          <h1 class="m-0 sop-title">SOP/TMR Billing</h1>
        </div>
      </div>
    </div>
  </div>

  <div class="stats-grid mb-3">
    <div class="metric-card">
      <div class="metric-head">Total SOP Billing</div>
      <div class="metric-value text-success">₹{{ formatMoney(totalSop()) }}</div>
      <div class="metric-foot">{{ sopBillCount() }} bills</div>
      <span class="metric-icon"><lucide-icon [img]="WalletIcon" [size]="18"></lucide-icon></span>
    </div>
    <div class="metric-card">
      <div class="metric-head">Total TMR Billing</div>
      <div class="metric-value">₹{{ formatMoney(totalTmr()) }}</div>
      <div class="metric-foot">{{ tmrBillCount() }} bills</div>
      <span class="metric-icon"><lucide-icon [img]="BarChart3Icon" [size]="18"></lucide-icon></span>
    </div>
    <div class="metric-card">
      <div class="metric-head">Bills Generated</div>
      <div class="metric-value">{{ generatedCount() }}</div>
      <div class="metric-foot">{{ mode() === 'sop' ? 'SOP' : 'TMR' }} ready bills</div>
      <span class="metric-icon"><lucide-icon [img]="ReceiptTextIcon" [size]="18"></lucide-icon></span>
    </div>
    <div class="metric-card">
      <div class="metric-head">Bills Sent</div>
      <div class="metric-value">{{ sentCount() }}</div>
      <div class="metric-foot">Pending payment follow-up</div>
      <span class="metric-icon"><lucide-icon [img]="SendIcon" [size]="18"></lucide-icon></span>
    </div>
  </div>

  <div class="panel-wrap">
    <div class="mode-switch" role="tablist" aria-label="SOP and TMR billing tabs">
      <button type="button" class="mode-btn" [class.active]="mode() === 'sop'" (click)="setMode('sop')">Sale of Power Bills</button>
      <button type="button" class="mode-btn" [class.active]="mode() === 'tmr'" (click)="setMode('tmr')">Trading Margin Bills</button>
    </div>
    <div class="toolbar-actions toolbar-actions--inline">
      <div class="filter-field">
        <label class="filter-label">Client</label>
        <select class="filter-control" [value]="clientFilter()" (change)="onClientFilterChange(($any($event.target).value || '').toString())">
          <option value="">All clients</option>
          @for (client of clientOptions(); track client.id) {
            <option [value]="client.id">{{ client.name }}</option>
          }
        </select>
      </div>
      <div class="search-wrap search-wrap--toolbar">
        <label class="small fw-semibold mb-1 d-block">Search {{ mode() === 'sop' ? 'SOP' : 'TMR' }} bills</label>
        <div class="position-relative">
          <input class="form-control form-control-sm pe-4 filter-control search-control" placeholder="Bill ID, LOA, buyer, period..." [value]="search()" (input)="onSearch(($any($event.target).value || '').toString())" />
          <lucide-icon [img]="SearchIcon" [size]="14" class="search-icon"></lucide-icon>
        </div>
      </div>
      <button type="button" class="action-btn filter-toggle-btn" (click)="toggleFilters()">
        <lucide-icon [img]="FilterIcon" [size]="14"></lucide-icon>
        <span>Filter</span>
      </button>
      @if (showFilters()) {
        <div class="filter-field">
          <label class="filter-label">Status</label>
          <select class="filter-control" [value]="draftStatus()" (change)="setDraftStatus(($any($event.target).value || 'all').toString())">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="generated">Generated</option>
            <option value="approved">Approved</option>
          </select>
        </div>
        <div class="filter-field">
          <label class="filter-label">From Date</label>
          <input class="filter-control" type="date" [value]="draftFromDate()" (input)="onDraftFromDateChange(($any($event.target).value || '').toString())" />
        </div>
        <div class="filter-field">
          <label class="filter-label">To Date</label>
          <input class="filter-control" type="date" [value]="draftToDate()" (input)="onDraftToDateChange(($any($event.target).value || '').toString())" />
        </div>
        <button type="button" class="action-btn go-btn" (click)="applyToolbarFilters()">Go</button>
      }
      <button type="button" class="action-btn merge-outline-btn" (click)="mergeSelectedBills()" [disabled]="selectedCount() < 2">Merge Bill</button>
      <button type="button" class="action-btn export-btn" (click)="exportFilteredRows()">
        <lucide-icon [img]="DownloadIcon" [size]="14"></lucide-icon>
        <span>Export Excel</span>
      </button>
    </div>
  </div>

  <div class="selection-strip">
    <div class="selection-copy">
      <strong>{{ selectedCount() }}</strong> bill{{ selectedCount() === 1 ? '' : 's' }} selected for merge
    </div>
    <div class="selection-note">Merge is enabled only when selected rows belong to the same buyer and seller.</div>
  </div>

  <div class="table-wrap">
    <div style="max-height: 500px; overflow-y: auto;">
      <table class="table table-sm bill-table mb-0">
        <thead class="table-head sticky-top">
          <tr>
            <th class="col-select" style="width:56px">Select</th>
            <th class="col-serial" style="width:70px">S.No</th>
            <th class="col-apps" style="width:96px">Apps</th>
            <th class="col-bill" style="min-width:170px">Bill ID</th>
            <th class="col-loa" style="min-width:150px">LOA ID</th>
            <th class="col-contract" style="min-width:150px">Contract ID</th>
            <th class="col-cycle" style="min-width:120px">Bill Cycle</th>
            <th class="col-buyer" style="min-width:190px">Seller</th>
            <th class="col-buyer" style="min-width:190px">Buyer</th>
            <th class="col-period" style="min-width:180px">Period</th>
            <th class="col-qty col-scheduled" style="min-width:170px">Approved (kWh)</th>
            <th class="col-qty col-rldc" style="min-width:170px">Scheduled (kWh)</th>
            <th class="col-rate" style="min-width:100px">Rate</th>
            <th class="col-net" style="min-width:150px">Net Payable</th>
            <th class="col-approval" style="width:90px; min-width:90px">Approval</th>
            <th class="col-actions" style="width:150px; min-width:150px">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="16" class="text-center text-muted py-4">Loading SOP/TMR Billing...</td></tr>
          } @else if (filteredRows().length === 0) {
            <tr><td colspan="16" class="text-center text-muted py-4">No bills found for the selected billing mode.</td></tr>
          }
          @for (row of pagedRows(); track row.rowKey; let i = $index) {
            <tr>
              <td class="cell-select">
                <input
                  type="checkbox"
                  [checked]="isSelected(row.rowKey)"
                  [disabled]="row.status === 'Generated' || !row.canGenerate"
                  (change)="toggleSelection(row.rowKey, $any($event.target).checked)"
                />
              </td>
              <td class="cell-serial">{{ (currentPage() - 1) * pageSize() + i + 1 }}</td>
              <td class="cell-apps">
                <div class="id-tooltip-wrap" tabindex="0">
                  <span class="table-count-badge">{{ relatedApplicationNumbers(row).length }}</span>
                  <div class="id-tooltip">
                    <div class="id-tooltip-title">Application Numbers</div>
                    @if (relatedApplicationNumbers(row).length) {
                      @for (appNo of relatedApplicationNumbers(row); track appNo) {
                        <div class="id-tooltip-line">{{ appNo }}</div>
                      }
                    } @else {
                      <div class="id-tooltip-line">No applications</div>
                    }
                  </div>
                </div>
              </td>
              <td class="fw-semibold cell-bill">{{ row.billId || '-' }}</td>
              <td class="link-blue cell-loa">
                <div class="id-tooltip-wrap" tabindex="0">
                  <span>{{ row.loiId }}</span>
                  <div class="id-tooltip">
                    <div class="id-tooltip-title">LOA Details</div>
                    <div class="id-tooltip-line">{{ row.loiId }}</div>
                    <div class="id-tooltip-line">Buyer: {{ row.buyer }}</div>
                    <div class="id-tooltip-line">Period: {{ row.period }}</div>
                  </div>
                </div>
              </td>
              <td class="cell-contract">{{ row.contractId }}</td>
              <td class="cell-cycle"><span class="cycle-chip">{{ row.billCycle }}</span></td>
              <td class="buyer-name cell-buyer">{{ row.seller }}</td>
              <td class="buyer-name cell-buyer">{{ row.buyer }}</td>
              <td class="cell-period">{{ row.period }}</td>
              <td class="cell-qty cell-scheduled">
                <div class="id-tooltip-wrap" tabindex="0">
                  <span>{{ formatMoney(row.scheduled) }} KWh</span>
                  <div class="id-tooltip">
                    <div class="id-tooltip-title">Scheduled Quantum Details</div>
                    @for (line of row.scheduledTooltipLines; track line) {
                      <div class="id-tooltip-line">{{ line }}</div>
                    }
                  </div>
                </div>
              </td>
              <td class="fw-semibold cell-qty cell-rldc">
                <div class="id-tooltip-wrap" tabindex="0">
                  <span>{{ formatRldcActual(row.rldcActual) }}</span>
                  <div class="id-tooltip">
                    <div class="id-tooltip-title">RLDC Approval Calculation</div>
                    @for (line of row.rldcTooltipLines; track line) {
                      <div class="id-tooltip-line">{{ line }}</div>
                    }
                  </div>
                </div>
              </td>
              <td class="cell-rate">₹{{ row.rate.toFixed(2) }}</td>
              <td class="net-pay cell-net">
                <div class="id-tooltip-wrap net-pay-tooltip" tabindex="0">
                  <span>₹{{ formatMoney(row.netPayable) }}</span>
                  <div class="id-tooltip">
                    <div class="id-tooltip-title">Net Payable Calculation</div>
                    @for (line of netPayableTooltipLines(row); track line) {
                      <div class="id-tooltip-line">{{ line }}</div>
                    }
                  </div>
                </div>
              </td>
              <td class="cell-approval">
                @if (!row.approvalAvailable) {
                  <span class="approval-warning-wrap" tabindex="0" aria-label="Approval not found">
                    <span class="approval-state approval-state-missing" aria-hidden="true">✕</span>
                    <span class="approval-warning-tooltip">Approval not found</span>
                  </span>
                } @else {
                  <span class="approval-ok-wrap" aria-label="Approval found">
                    <span class="approval-state approval-state-found" aria-hidden="true">✓</span>
                  </span>
                }
              </td>
              <td class="cell-actions">
                <div class="action-group">
                  @if (row.status === 'Generated' && row.billId) {
                    <button type="button" class="icon-btn" title="Download Invoice" (click)="downloadGeneratedInvoice(row)">
                      <lucide-icon [img]="DownloadIcon" [size]="14"></lucide-icon>
                    </button>
                  } @else {
                    <button type="button" class="icon-btn" title="Preview Invoice" (click)="viewInvoice(row)">
                      <lucide-icon [img]="EyeIcon" [size]="14"></lucide-icon>
                    </button>
                    <button type="button" class="btn btn-sm btn-primary generate-btn" [disabled]="!row.canGenerate || row.status === 'Generated'" [title]="row.approvalAvailable ? 'Generate bill' : 'Approval not available'" (click)="requestGenerateBill(row)">
                      Generate
                    </button>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    <div class="pager mt-3">
      <div class="pagination-shell">
        <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="page.set(1)" aria-label="First page">
          <lucide-icon [img]="ChevronsLeftIcon" [size]="14"></lucide-icon>
        </button>
        <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="page.set(currentPage() - 1)" aria-label="Previous page">
          <lucide-icon [img]="ChevronLeftIcon" [size]="14"></lucide-icon>
        </button>
        <span class="page-indicator current">{{ currentPage() }}</span>
        <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="page.set(currentPage() + 1)" aria-label="Next page">
          <lucide-icon [img]="ChevronRightIcon" [size]="14"></lucide-icon>
        </button>
        <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="page.set(totalPages())" aria-label="Last page">
          <lucide-icon [img]="ChevronsRightIcon" [size]="14"></lucide-icon>
        </button>
        <select class="form-select form-select-sm page-size-select pager-size-select" [value]="pageSize()" (change)="onPageSizeChange(($any($event.target).value || '10').toString())" aria-label="Rows per page">
          @for (size of pageSizes; track size) {
            <option [value]="size">{{ size }}/Page</option>
          }
        </select>
      </div>
    </div>
  </div>

  
</div>

@if (showPreviewModal()) {
  <div class="modal fade show d-block invoice-preview-modal" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-xl modal-dialog-centered invoice-preview-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Invoice Preview</h5>
          <button type="button" class="btn-close" aria-label="Close" (click)="closePreviewModal()"></button>
        </div>
        <div class="modal-body invoice-preview-body">
          @if (previewInvoiceRow(); as row) {
            <div class="pdf-page pdf-hidden" id="pdfContent">
              <div id="formatD" class="pdf-page">
                <h5 class="heading text-center mb-0">Bill of Supply</h5>
                <table class="info-table">
                <colgroup>
                  <col style="width: 5.5%" />
                  <col style="width: 5.5%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 8%" />
                  <col style="width: 11%" />
                  <col style="width: 11%" />
                  <col style="width: 11%" />
                </colgroup>
                <tbody>
                  <tr>
                    <td colspan="3" class="w-10 align-middle text-center logo-cell">
                      <img src="/sharajman-logo.svg" alt="Sharajman" class="invoice-logo" />
                    </td>
                    <td colspan="5" class="w-40 align-top p-2">
                      <strong>Address</strong><br />
                      {{ issuerProfile.companyName }}<br />
                      {{ issuerProfile.address }}<br />
                      <strong>PAN:</strong> {{ issuerProfile.pan }}<br />
                      <strong>GSTIN/UIN:</strong> {{ issuerProfile.gst }}<br />
                      <strong>State:</strong> {{ issuerProfile.state }} <strong>Code:</strong> {{ issuerProfile.stateCode }}<br />
                      <strong>CIN:</strong> {{ issuerProfile.cin }}<br />
                    </td>
                    <td colspan="4" class="p-1">
                      <table class="nested-table">
                        <tbody>
                          <tr>
                            <th scope="row" style="display:none;">Invoice and Billing Date</th>
                            <td><span class="fw-bold">Invoice</span><br />{{ row.billId || '\u00A0' }}</td>
                            <td><span class="fw-bold">Billing Date</span><br />{{ invoiceIssueDate() || '\u00A0' }}</td>
                          </tr>
                          <tr>
                            <th scope="row" style="display:none;">Due Date and Financial Year</th>
                            <td><span class="fw-bold">Due Date</span><br />{{ invoiceDueDate() || '\u00A0' }}</td>
                            <td><span class="fw-bold">Financial Year</span><br />{{ invoiceFinancialYear() || '\u00A0' }}</td>
                          </tr>
                          <tr>
                            <th scope="row" style="display:none;">From Date and To Date</th>
                            <td><span class="fw-bold">From Date</span><br />{{ invoiceFromDate(row) || '\u00A0' }}</td>
                            <td><span class="fw-bold">To Date</span><br />{{ invoiceToDate(row) || '\u00A0' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="9" class="align-top p-2">
                      <strong>Consignee (Ship To)</strong><br />
                      {{ row.seller }}<br />
                      -<br />
                      <strong>GSTIN:</strong> -<br />
                      <strong>PAN:</strong> -<br />
                      <strong>State:</strong> - <strong>Code:</strong> -<br />
                      <strong>Place of Supply:</strong> -
                    </td>
                    <td colspan="3" class="align-top p-2">
                      <strong>Buyer (Bill To)</strong><br />
                      {{ row.buyer }}<br />
                      {{ row.buyerAddress }}<br />
                      <strong>GSTIN:</strong> -<br />
                      <strong>PAN:</strong> -<br />
                      <strong>State:</strong> - <strong>Code:</strong> -<br />
                      <strong>Place of Supply:</strong> -
                    </td>
                  </tr>
                  <tr class="align-top text-center">
                    <th id="sn">S.No.</th>
                    <th colspan="5" id="partyname">Description</th>
                    <th id="hsnsac">HSN/SAC</th>
                    <th id="partyquant">Quantity(MWh)</th>
                    <th id="rate">Rate(Rs)</th>
                    <th id="per">Per</th>
                    <th class="text-end" colspan="2" id="partyamt">Amount</th>
                  </tr>
                  <tr class="align-top">
                    <td>1</td>
                    <td colspan="5">{{ row.mode === 'sop' ? 'POWER SUPPLY' : 'TRADING MARGIN' }} FOR THE PERIOD {{ row.period }}</td>
                    <td>27160000</td>
                    <td>{{ invoiceQty(row.billedQuantity) }}</td>
                    <td>{{ invoiceRate(row.rate) }}</td>
                    <td>Units</td>
                    <td colspan="2">Rs. {{ invoiceAmount(row.netPayable) }}</td>
                  </tr>
                  <tr>
                    <td colspan="10"><strong>Total</strong></td>
                    <td colspan="2"><strong>Rs. {{ invoiceAmount(row.netPayable) }}</strong></td>
                  </tr>
                  <tr class="amount-words">
                    <td colspan="12">
                      <strong>Amount Chargeable (in words)</strong>
                      <span class="float-right">E. &amp; O.E</span>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="12">INR {{ invoiceWords(row.netPayable) }}</td>
                  </tr>
                  <tr>
                    <th id="hsn" colspan="8">HSN/SAC</th>
                    <th id="taxable" colspan="4">Taxable Value</th>
                  </tr>
                  <tr>
                    <td id="hsn" colspan="8">27160000</td>
                    <td id="taxable" colspan="4">{{ invoiceAmount(row.netPayable) }}</td>
                  </tr>
                  <tr>
                    <td id="hsn" colspan="8">Total</td>
                    <td id="taxable" colspan="4">Rs. {{ invoiceAmount(row.netPayable) }}</td>
                  </tr>
                  <tr>
                    <td colspan="8">
                      <strong>Total Amount (in Words):</strong> INR {{ invoiceWords(row.netPayable) }}<br />
                      <strong>Company's PAN:</strong> -
                    </td>
                    <td colspan="4" rowspan="2">
                      <strong>Company's Bank Details</strong><br />
                      <b>A/c Holder's Name</b> : SHARAJMAN TECHNOLOGY<br />
                      <b>Bank Name</b> : -<br />
                      <b>A/c No.</b> : -<br />
                      <b>Branch &amp; IFSC Code</b> : - &amp; -<br />
                      <b>SWIFT Code</b> : -
                    </td>
                  </tr>
                  <tr>
                    <td colspan="8">
                      <strong>Declaration</strong><br />
                      We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                    </td>
                  </tr>
                </tbody>
                </table>

                <div class="mt-2">
                  <div class="col-5 offset-7 border">
                    <div class="fw-bold text-center">{{ issuerProfile.companyName }}</div>
                    <div class="signature-box my-2 ms-auto"></div>
                    <div class="fw-bold text-center">Authorised Signatory</div>
                  </div>
                </div>

                <div class="row border-top mt-2 pt-2 text-center small fw-bold">
                  <div>Corporate Address : {{ issuerProfile.address }}</div>
                  <div class="fw-normal">Registered Address : {{ issuerProfile.address }}</div>
                  <div class="fw-normal">CIN: {{ issuerProfile.cin }} | PAN: {{ issuerProfile.pan }} | GSTIN: {{ issuerProfile.gst }}</div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" (click)="closePreviewModal()"></div>
}

@if (showGenerateConfirmModal() && generateConfirmRow(); as row) {
  <div class="bill-confirm-backdrop" (click)="cancelGenerateBill()">
    <div class="bill-confirm-modal" (click)="$event.stopPropagation()">
      <div class="bill-confirm-body">
        <h3 class="m-0">Generate {{ row.mode === 'sop' ? 'SOP' : 'TMR' }} Bill</h3>
        <p class="mb-0 mt-2">
          Generate bill for <strong>{{ row.buyer }}</strong> for the period <strong>{{ row.period }}</strong>?
        </p>
      </div>
      <div class="bill-confirm-actions">
        <button type="button" class="btn btn-light" (click)="cancelGenerateBill()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="confirmGenerateBill()">Generate</button>
      </div>
    </div>
  </div>
}
